<script lang="ts">
  import {
    problemDetailStore,
    patchProblem,
  } from '@entities/problem/store.svelte';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import { canEdit } from '@shared/types/session';
  import { isProblemLocked } from '@domain/problem/locked-columns';
  import InlineField from '@shared/ui/InlineField.svelte';
  import ProblemCategorySelect from '@shared/ui/ProblemCategorySelect.svelte';
  import ProblemThemeSelect from '@shared/ui/ProblemThemeSelect.svelte';
  import { confirmAndDeleteProblem } from '@features/problem-delete/problem-delete';
  import { navigate } from '@shared/router/navigation';
  import type { ProblemPatchRequest } from '@shared/types/problem';

  interface Props {
    id: string;
  }

  let { id }: Props = $props();

  let deleteError = $state<string | null>(null);
  let draftProblemId = $state('');
  let draftCategory = $state('');
  let draftTheme = $state('');
  let hierarchyDirty = $state(false);

  $effect(() => {
    void problemDetailStore.load(id);
    return () => {
      problemDetailStore.clear();
    };
  });

  const allowEdit = $derived(
    sessionStore.user ? canEdit(sessionStore.user.role) : false,
  );

  $effect(() => {
    const p = problemDetailStore.data;
    if (!p) {
      draftProblemId = '';
      draftCategory = '';
      draftTheme = '';
      hierarchyDirty = false;
      return;
    }

    if (draftProblemId !== p.id || !hierarchyDirty) {
      draftProblemId = p.id;
      draftCategory = p.category ?? '';
      draftTheme = p.theme ?? '';
      hierarchyDirty = false;
    }
  });

  type EditableTextKey = 'name' | 'detection_keywords';

  async function saveText(field: EditableTextKey, next: string): Promise<void> {
    const body: ProblemPatchRequest = {
      [field]: next.length > 0 ? next : null,
    };
    await patchProblem(id, body);
  }

  function selectCategory(next: string): void {
    draftCategory = next;
    draftTheme = '';
    hierarchyDirty = true;
  }

  async function saveTheme(next: string): Promise<void> {
    const category = draftCategory.trim();
    const theme = next.trim();
    if (category.length === 0 || theme.length === 0) {
      throw new Error('大カテゴリと中テーマを選択してください');
    }
    await patchProblem(id, { category, theme });
    draftCategory = category;
    draftTheme = theme;
    hierarchyDirty = false;
  }

  /// 推認属性 (detection_signals) は TEXT[] (catalog No.127「リスト入力」)。
  /// textarea を 1 行 1 属性として扱い、 空なら null クリア。
  async function saveDetectionSignals(next: string): Promise<void> {
    const items = next
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    await patchProblem(id, {
      detection_signals: items.length > 0 ? items : null,
    });
  }

  function locked(field: string): boolean {
    return isProblemLocked(field);
  }

  async function handleDelete(name: string): Promise<void> {
    deleteError = null;
    try {
      const ok = await confirmAndDeleteProblem({ id, name });
      if (ok) navigate('/master/problems');
    } catch (err) {
      deleteError = err instanceof Error ? err.message : String(err);
    }
  }
</script>

<a class="back" href="/master/problems">← 課題一覧へ</a>

{#if problemDetailStore.loading && problemDetailStore.data === null}
  <p class="status">読み込み中…</p>
{:else if problemDetailStore.error}
  <p class="status error">{problemDetailStore.error}</p>
{:else if problemDetailStore.data}
  {@const p = problemDetailStore.data}
  <header class="page-header">
    <p class="eyebrow">課題</p>
    <h1>{p.name}</h1>
  </header>

  <div class="detail">
    <section>
      <h3>基本情報</h3>
      <div class="grid">
        <InlineField
          label="小課題名"
          value={p.name}
          type="text"
          locked={locked('name')}
          disabled={!allowEdit}
          onSave={(next) => saveText('name', next)}
        />
        <div class="inline-field">
          <span class="field-label">大カテゴリ</span>
          <div class="category-control">
            <ProblemCategorySelect
              value={draftCategory}
              onChange={selectCategory}
              allowAdd={allowEdit}
              disabled={!allowEdit || locked('category')}
            />
            {#if hierarchyDirty}
              <small class="hint">中テーマを選択すると保存されます</small>
            {/if}
          </div>
        </div>
        <div class="inline-field">
          <span class="field-label">中テーマ</span>
          <div class="category-control">
            <ProblemThemeSelect
              category={draftCategory}
              value={draftTheme}
              onChange={saveTheme}
              allowAdd={allowEdit}
              disabled={!allowEdit || locked('theme')}
            />
          </div>
        </div>
        <InlineField
          label="推認属性"
          value={(p.detection_signals ?? []).join('\n')}
          type="textarea"
          placeholder={'1 行 1 属性。 例: ITリテラシー=低\n利用ITツール=Excel中心'}
          locked={locked('detection_signals')}
          disabled={!allowEdit}
          onSave={saveDetectionSignals}
        />
        <InlineField
          label="推認キーワード"
          value={p.detection_keywords}
          type="textarea"
          placeholder="カンマ or 改行区切り。 例: 手作業, Excel, 属人化"
          locked={locked('detection_keywords')}
          disabled={!allowEdit}
          onSave={(next) => saveText('detection_keywords', next)}
        />
      </div>
    </section>

    {#if allowEdit}
      <section class="danger-zone">
        <h3>危険操作</h3>
        <p class="muted small">課題を削除すると元に戻せません。</p>
        {#if deleteError}<p class="status error">{deleteError}</p>{/if}
        <button
          type="button"
          class="btn-delete"
          onclick={() => handleDelete(p.name)}
        >
          この課題を削除
        </button>
      </section>
    {/if}
  </div>
{/if}

<style>
  .back {
    display: inline-block;
    margin-bottom: 8px;
    color: #2563eb;
    text-decoration: none;
    font-size: 0.875rem;
  }
  .back:hover {
    text-decoration: underline;
  }
  .page-header {
    margin: 0 0 16px;
    padding: 16px;
    background: #fff;
    border: 1px solid #dfe3ea;
    border-radius: 8px;
  }
  .eyebrow {
    margin: 0 0 4px;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
  }
  h1 {
    margin: 0;
    font-size: 22px;
  }
  .status {
    color: #6b7280;
  }
  .status.error {
    color: #b91c1c;
  }
  .detail {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  section {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 16px;
  }
  h3 {
    margin: 0 0 12px;
    font-size: 0.875rem;
    color: #374151;
  }
  /* LawyerDetail と同じ kv 2-column inline grid */
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px 16px;
  }
  .grid :global(.inline-field) {
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: baseline;
    gap: 4px 16px;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .grid :global(.inline-field > .field-label) {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 700;
  }
  .category-control {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .hint {
    color: #6b7280;
    font-size: 0.75rem;
  }
  .danger-zone {
    border-color: #fecaca;
  }
  .danger-zone h3 {
    color: #b91c1c;
  }
  .small {
    font-size: 0.75rem;
  }
  .muted {
    color: #6b7280;
  }
  .btn-delete {
    margin-top: 8px;
    padding: 6px 12px;
    border: 1px solid #dc2626;
    border-radius: 4px;
    background: #fff;
    color: #dc2626;
    font: inherit;
    cursor: pointer;
  }
  .btn-delete:hover {
    background: #fef2f2;
  }
</style>
